import { useEffect, useCallback, useRef } from 'react';
import { toastError } from '@/components/ui/use-toast';
import { getAutoTradingNotifications, type AutoTradingNotification } from '../services/api';
import { useAuth } from '@/contexts/AuthContext';
import usePaperTrading from './usePaperTrading';
import { useTranslation } from 'react-i18next';

type BatchAutoTradingNotifications = {
  notifications?: AutoTradingNotification[];
  count?: number;
  error?: string | {
    code: string;
    message?: string;
  };
} | null | undefined;

export const useAutoTradingNotifications = (batchNotifications?: BatchAutoTradingNotifications) => {
  const { isAuthenticated } = useAuth();
  const { isPaperMode } = usePaperTrading();
  const { t } = useTranslation();
  const shownOnceRef = useRef(false);

  // Map backend reason strings to translation keys
  const getTranslatedReason = useCallback((reason: string): string => {
    // Check for specific backend reason patterns and map to translation keys
    if (reason.includes('No exchange API credentials configured')) {
      return t('notifications.autoTrading.noApiCredentialsReason');
    }
    if (reason.includes('Investment percentage is set to 0%')) {
      return t('notifications.autoTrading.investmentPercentageReason');
    }
    // Fallback to original reason if no mapping found
    return reason;
  }, [t]);

  const checkNotifications = useCallback(async () => {
    // Only check notifications if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    try {
      // Suppress completely when in Paper Trading mode
      if (isPaperMode) return;

      // Only once per page load/session
      if (shownOnceRef.current) return;

      let notificationsSource: AutoTradingNotification[] | undefined;

      if (batchNotifications) {
        if (batchNotifications.error) {
          console.warn('[useAutoTradingNotifications] Batch notifications error', batchNotifications.error);
        } else if (Array.isArray(batchNotifications.notifications)) {
          notificationsSource = batchNotifications.notifications;
        }
      }

      if (!notificationsSource) {
        const data = await getAutoTradingNotifications();
        notificationsSource = Array.isArray(data.notifications) ? data.notifications : [];
      }

      // Show at most one recent notification (within last 15 minutes)
      const cutoff = new Date(Date.now() - 15 * 60 * 1000);
      const notifications: AutoTradingNotification[] = notificationsSource || [];
      const recent = notifications.filter((n: AutoTradingNotification) => {
        const ts = new Date(n.timestamp);
        return !Number.isNaN(ts.getTime()) && ts > cutoff;
      });
      if (recent.length > 0) {
        const n = recent[0];
        toastError({
          title: t('notifications.autoTrading.systemDisabledTitle'),
          description: getTranslatedReason(n.reason),
        });
        shownOnceRef.current = true;
      }

    } catch (error) {
      console.error('Failed to check auto-trading notifications:', error);
    }
  }, [batchNotifications, isAuthenticated, isPaperMode, t, getTranslatedReason]);

  useEffect(() => {
    // Only set up notifications checking if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Check once on mount; do not repeat to avoid spam
    checkNotifications();

    // No interval to avoid repeated toasts
  }, [checkNotifications, isAuthenticated, batchNotifications]);

  return { checkNotifications };
};
